// components/shared/Chatbot.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';

export function Chatbot({ user, prescriptions = [], orders = [], onNavigate }) {
  const { brand } = useBrand();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);
  
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [isOpen]);
  
  const buildSystemPrompt = () => {
    const isB2B = brand.isB2B;
    
    if (isB2B) {
      return `You are the AI procurement assistant for ${brand.name}. Help with procurement questions, catalog search, spend analysis, and order tracking.

USER: ${user.firstName} ${user.lastName} (${user.email})

GUIDELINES:
- Be helpful, concise, and professional
- Reference specific data when available
- Suggest navigation with [[nav:PAGE_KEY:Label]] format
- Valid pages: dashboard, vendors, catalog, orders, savings, profile
- Keep responses under 150 words

Example navigation: [[nav:catalog:Browse Catalog]]`;
    } else {
      return `You are the AI patient assistant for ${brand.name}. Help patients with prescription questions, refill scheduling, order tracking, and cost savings.

USER: ${user.firstName} ${user.lastName} (${user.email})

PRESCRIPTIONS:
${prescriptions.filter(rx => rx.userId === user.id).map(rx => 
    `${rx.id}: ${rx.medication} | Status: ${rx.status} | Refills: ${rx.refills || 0}`
  ).join('\n') || 'No prescriptions yet'}

ORDERS:
${orders.filter(o => o.userId === user.id).map(o => 
    `${o.id}: ${o.prescriptionIds?.length || 0} items | Status: ${o.status}`
  ).join('\n') || 'No orders yet'}

GUIDELINES:
- Be helpful, concise, and professional
- Reference specific data when available
- Suggest navigation with [[nav:PAGE_KEY:Label]] format
- Valid pages: dashboard, prescriptions, orders, upload, bundle, savings, messages, profile
- Keep responses under 150 words

Example navigation: [[nav:upload:Upload Prescription]]`;
    }
  };
  
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    
    try {
      const apiBase = window.location.origin;
      const response = await fetch(`${apiBase}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: buildSystemPrompt(),
          messages: newMessages
        })
      });
      
      const data = await response.json();
      const reply = data.content?.map(b => b.type === 'text' ? b.text : '').join('') || 
                    "I'm sorry, I couldn't process that. Please try again.";
      
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Connection error. Please try again in a moment.' 
      }]);
    } finally {
      setLoading(false);
    }
  };
  
  const parseMessage = (text) => {
    const parts = [];
    const regex = /\[\[nav:(\w+):([^\]]+)\]\]/g;
    let lastIndex = 0;
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'nav', page: match[1], label: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length ? parts : [{ type: 'text', content: text }];
  };
  
  const quickActions = brand.isB2B
    ? [
      { text: 'Vendor sync help', query: "I'm having trouble syncing my vendor account" },
      { text: 'Compare pricing', query: 'Show me vendor price comparisons' },
      { text: 'GPO savings', query: 'How much are we saving vs GPO?' }
    ]
    : [
      { text: 'Next refill due', query: 'When is my next prescription refill due?' },
      { text: 'Cost savings', query: 'How much am I saving vs retail?' },
      { text: 'Order status', query: "What's the status of my most recent order?" }
    ];
  
  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 56,
          height: 56,
          borderRadius: 16,
          background: isOpen ? '#6B7280' : `linear-gradient(135deg, ${brand.c.accent}, ${brand.c.navy})`,
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 24px rgba(0,0,0,0.25)',
          zIndex: 999,
          transition: 'all 0.2s'
        }}
      >
        <Icon name={isOpen ? 'x' : 'msgCircle'} size={24} color="white" />
      </button>
      
      {/* Chat Panel */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: 90,
          right: 24,
          width: 380,
          maxWidth: 'calc(100vw - 48px)',
          height: 600,
          maxHeight: 'calc(100vh - 120px)',
          background: 'white',
          borderRadius: 16,
          boxShadow: '0 12px 48px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 998,
          animation: 'chatSlide 0.3s ease-out',
          fontFamily: 'Libre Franklin, sans-serif'
        }}>
          {/* Header */}
          <div style={{
            padding: 16,
            borderBottom: '1px solid #E5E7EB',
            background: brand.c.navy,
            borderRadius: '16px 16px 0 0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                background: brand.c.accent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Icon name="sparkles" size={20} color="white" />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'white' }}>
                  {brand.name} Assistant
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  AI-powered support
                </div>
              </div>
            </div>
          </div>
          
          {/* Messages */}
          <div 
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: 16,
              display: 'flex',
              flexDirection: 'column',
              gap: 12
            }}
          >
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 20 }}>
                  {brand.isB2B 
                    ? 'Ask about pricing, orders, catalog, or spend analysis'
                    : 'Ask about your prescriptions, orders, or savings'
                  }
                </div>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  justifyContent: 'center'
                }}>
                  {quickActions.map((action, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setInput(action.query);
                        setTimeout(() => sendMessage(), 100);
                      }}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: `1px solid ${brand.c.accent}`,
                        background: 'white',
                        fontSize: 12,
                        fontWeight: 600,
                        color: brand.c.accent,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => {
                        e.target.style.background = `${brand.c.accent}08`;
                      }}
                      onMouseLeave={e => {
                        e.target.style.background = 'white';
                      }}
                    >
                      {action.text}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                  background: msg.role === 'user' ? brand.c.navy : '#F3F4F6',
                  color: msg.role === 'user' ? 'white' : '#111827',
                  fontSize: 14,
                  lineHeight: 1.5
                }}>
                  {parseMessage(msg.content).map((part, j) => 
                    part.type === 'text' ? (
                      <span key={j}>{part.content}</span>
                    ) : (
                      <button
                        key={j}
                        onClick={() => {
                          onNavigate(part.page);
                          setIsOpen(false);
                        }}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          padding: '4px 8px',
                          margin: '4px 0',
                          borderRadius: 6,
                          border: `1px solid ${brand.c.accent}`,
                          background: 'white',
                          color: brand.c.accent,
                          fontSize: 13,
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {part.label}
                        <Icon name="arrowRight" size={12} color={brand.c.accent} />
                      </button>
                    )
                  )}
                </div>
              </div>
            ))}
            
            {loading && (
              <div style={{ display: 'flex', gap: 4, padding: 10 }}>
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: '#9CA3AF',
                      animation: `dotPulse 1.4s infinite`,
                      animationDelay: `${i * 0.2}s`
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Input */}
          <div style={{
            padding: 16,
            borderTop: '1px solid #E5E7EB'
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && sendMessage()}
                placeholder="Type your message..."
                disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  border: '2px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Libre Franklin, sans-serif'
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || loading}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: input.trim() && !loading ? brand.c.accent : '#E5E7EB',
                  color: 'white',
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon name="send" size={18} color="white" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes chatSlide {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
      `}</style>
    </>
  );
}
